import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ReduxStates } from '../../../../redux/reduxTypes';

import Highcharts from 'highcharts';
import HighchartsWrapper from '../../../../components/HighchartsWrapper';
import HighchartsExporting from 'highcharts/modules/exporting';

import { baseGraphConfigs } from './configs';
import { intToString, returnCurrencySymbol } from '../../../../helpers/format';

// extends export functionality to the main highcharts library
if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
}

import GraphLegend from '../../../../components/GraphLegend';
import { GraphData as GraphDataInterface } from '../../../../types';
import constants from '../../../../helpers/constants';

interface Props {
  graphData: any;
  combinedGraphEl: any;
  showDownloadOptions: boolean;
  setShowDownloadOptions: (value: React.SetStateAction<boolean>) => void;
  selectedCurrency?: string;
  currencyCode?: string | null;
  unavailableData?: boolean;
  showPlanData?: boolean;
}

interface GraphData {
  category: string;
  sellingPrice?: number | string;
  currentYearPrice?: number | string;
  lastYearPrice?: number | string;
  plan?: number | string;
}

const legends: Highcharts.SeriesOptionsType[] = [
  {
    id: 'series-last-year',
    type: 'column',
    color: '#F7B74D',
    name: 'graph_last_year',
    data: []
  },
  {
    id: 'series-current-year',
    type: 'column',
    color: '#4D70B8',
    name: 'graph_current_year',
    data: []
  },
  {
    id: 'series-plan',
    type: 'column',
    color: '#E14A84',
    name: 'graph_plan',
    data: []
  },
  {
    id: 'series-yoy-change',
    type: 'line',
    color: '#141414',
    name: 'graph_yoy',
    data: []
  }
];

const Graph = ({
  graphData,
  combinedGraphEl,
  selectedCurrency,
  currencyCode,
  unavailableData,
  showPlanData
}: Props): React.ReactElement => {
  const [selectedGraphData, setSelectedGraphData] = useState<GraphData | null>(null);
  const copyData = useSelector((state: ReduxStates) => state.copyData);

  const handleMouseOver = (event: any) => {
    if (combinedGraphEl) {
      const combinedGraph: Highcharts.Chart = combinedGraphEl?.current?.chart;
      const normalizedEvent = combinedGraph.pointer.normalize(event.target);

      // @ts-ignore
      // const lastYearGraph = combinedGraph.series[0].data[normalizedEvent.index];
      const lastYearGraph = combinedGraph.get('series-last-year').data[normalizedEvent.index];
      // @ts-ignore
      // const currentYearGraph = combinedGraph.series[1].data[normalizedEvent.index];
      const currentYearGraph = combinedGraph.get('series-current-year').data[normalizedEvent.index];
      // @ts-ignore
      const plan = combinedGraph.get('series-plan').data[normalizedEvent.index];
      // @ts-ignore
      const sellingPriceGraph = combinedGraph.get('series-yoy-change').data[normalizedEvent.index];

      setSelectedGraphData({
        category: event.target.category,
        sellingPrice: sellingPriceGraph ? sellingPriceGraph.y : 'hidden',
        currentYearPrice: currentYearGraph ? currentYearGraph.y : 'hidden',
        lastYearPrice: lastYearGraph ? lastYearGraph.y : 'hidden',
        plan: showPlanData && plan ? plan?.y : 'hidden'
      });
    }
  };

  const handleValueORPercentage = (value: number | string | undefined) => {
    if (!value && value !== 0) return '--';
    //grab unit value type from graph data instead of from summary, in case they differ
    const isPercentage = graphData[0].currentValueType === 'percentage';
    const unit = isPercentage ? '%' : null;

    //create new number value for valid number testing, because type in Highcharts can be a string or number
    const vString = typeof value === 'undefined' ? '' : value.toString();
    const v: number = parseFloat(vString);

    if (value === Number.MAX_VALUE || !isFinite(v)) return '--';
    const valueString = value.toString();
    let withoutUnit: any = valueString;

    if (!isPercentage) {
      withoutUnit = unit
        ? value < 1
          ? valueString.substring(0, 3)
          : valueString.substring(0, 2)
        : intToString(Number(value), 1, false, currencyCode);
    }

    const withUnit = unit
      ? `${withoutUnit}${unit}`
      : currencyCode
      ? `${returnCurrencySymbol(selectedCurrency)}${withoutUnit}`
      : `${withoutUnit}`;

    return withUnit;
  };
  const renderTooltip = (): string => {
    if (!selectedGraphData) return '';

    const returnData = (price: any) => {
      return handleValueORPercentage(price);
    };

    return `
        <b>${selectedGraphData?.category}</b>
        <br/>
        ${
          selectedGraphData?.lastYearPrice !== 'hidden'
            ? `<span style="color:#F7B74D">\u25CF </span> &nbsp;
          <span>${copyData?.graph_last_year}: <b>${returnData(selectedGraphData?.lastYearPrice)}</b></span>
          <br/>`
            : ``
        }
        ${
          selectedGraphData?.currentYearPrice !== 'hidden'
            ? `<span style="color:#4D70B8">\u25CF </span> &nbsp;
          <span>${copyData?.graph_current_year}: <b>${returnData(selectedGraphData?.currentYearPrice)}</b></span>
          <br/>`
            : ``
        }
        ${
          showPlanData && selectedGraphData?.plan !== 'hidden'
            ? `<span style="color:#E14A84">\u25CF </span> &nbsp;
          <span>${copyData?.graph_plan}: <b>${returnData(selectedGraphData?.plan)}</b></span>
          <br/>`
            : ``
        }
        ${
          selectedGraphData?.sellingPrice !== 'hidden'
            ? `<span style="color:#141414">\u25CF </span> &nbsp;
          <span>${copyData?.graph_yoy}: <b>${
                selectedGraphData?.sellingPrice ? selectedGraphData?.sellingPrice + '%' : '--'
              }</b></span>
        <br/>`
            : ``
        }
      `;
  };

  const combinedGraphOptions: Highcharts.Options = {
    ...baseGraphConfigs,
    plotOptions: {
      ...baseGraphConfigs.plotOptions,
      column: {
        dataLabels: {
          enabled: true,
          formatter: function () {
            return handleValueORPercentage(this.y);
          }
        },
        pointPadding: 0
      },
      series: {
        states: {
          inactive: {
            opacity: 1
          }
        },
        point: {
          events: {
            mouseOver: (event) => {
              handleMouseOver(event);
            }
          }
        }
      }
    },
    tooltip: {
      formatter: renderTooltip,
      enabled: true,
      shared: true
    },
    xAxis: {
      labels: {
        enabled: true
      },
      categories: graphData?.map((g: GraphDataInterface) => g.date) || []
    },
    yAxis: [
      {
        labels: {
          formatter: function () {
            return handleValueORPercentage(this.value);
          }
        },
        title: undefined,
        opposite: false
      },
      {
        labels: {
          format: '{value}%'
        },
        title: undefined,
        opposite: true
      }
    ],
    series: [
      {
        type: 'column',
        color: '#F7B74D',
        data:
          graphData?.map((g: GraphDataInterface) => {
            return {
              y: g.previousYearValue,
              color: '#F7B74D',
              borderColor: '#F7B74D'
            };
          }) || [],
        name: copyData?.graph_last_year,
        id: 'series-last-year'
      },
      {
        type: 'column',
        color: '#4D70B8',
        data:
          graphData?.map((g: GraphDataInterface) => {
            return {
              y: g.currentValue,
              color: '#4D70B8',
              borderColor: '#4D70B8'
            };
          }) || [],
        name: copyData?.graph_current_year,
        id: 'series-current-year'
      },
      {
        type: 'column',
        color: '#E14A84',
        data:
          graphData?.map((g: GraphDataInterface) => {
            return {
              y: g.plan,
              color: '#E14A84',
              borderColor: '#E14A84'
            };
          }) || [],
        name: copyData?.graph_plan,
        id: 'series-plan'
      },
      {
        type: 'line',
        color: '#141414',
        yAxis: 1,
        data: graphData?.map((g: GraphDataInterface) => g.yoyChange) || [],
        name: copyData?.graph_yoy,
        id: 'series-yoy-change'
      }
    ]
  };

  const toggleVisibility = (visible: boolean, id: string) => {
    const combinedGraph: Highcharts.Chart = combinedGraphEl?.current?.chart;
    const series: any = combinedGraph.get(id);
    visible ? series?.show() : series?.hide();
  };

  // data updated
  useEffect(() => {
    const combinedGraph: Highcharts.Chart = combinedGraphEl?.current?.chart;
    const planSeries: any = combinedGraph.get('series-plan');
    showPlanData ? planSeries?.show() : planSeries?.hide();
  }, [graphData]);

  return (
    <>
      <div className="relative">
        {!unavailableData && !graphData ? (
          <div className="absolute inset-0 w-full h-full z-10 bg-white flex justify-center items-center">
            <p className={constants.noData}>{copyData?.loading}</p>
          </div>
        ) : (graphData && !graphData.length) || unavailableData ? (
          <div className="absolute inset-0 w-full h-full z-10 bg-white flex justify-center items-center">
            <p className={constants.noData}>{copyData?.no_data}</p>
          </div>
        ) : null}
        <HighchartsWrapper lineGraphEl={combinedGraphEl} graphOptions={combinedGraphOptions} />
      </div>
      <div className="w-full flex justify-center my-5">
        {legends.map((legend, idx) => {
          const legendComponent = (
            <GraphLegend
              key={idx}
              legend={legend}
              label={copyData ? copyData[legend.name as string] : ''}
              toggleVisibility={(value) => toggleVisibility(value, legend.id || '')}
            />
          );
          return legend.id === 'series-plan' ? (showPlanData ? legendComponent : false) : legendComponent;
        })}
      </div>
    </>
  );
};

export default Graph;
